import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { S3Image } from 'aws-amplify-react';
import { css } from '@emotion/core';
import { Overlay } from '@lego/klik-react';
import { Cross } from '@lego/klik-icons-react';
import { Spinner } from '../../spinner/Spinner';
import { spacing, colors } from '../../../config';
import { FileCategories, getFileType } from '../../../utils/getFileType'; // Assuming FileCategories is exported
import { Storage } from 'aws-amplify';

const imageViewerImageStyle = css({
  height: 'auto',
  width: '100%',
  display: 'block', // Ensure image/iframe takes up space
});

const pdfViewerStyle = css({
  width: '100%',
  height: 'calc(100vh - 160px)', // Adjusted height: original 100px + 60px margin
  border: 'none',
  marginTop: '60px', // Added margin to push iframe down
});

const imageViewerContainerStyle = css({
  maxWidth: 1200,
  position: 'relative',
  margin: '0 auto',
  padding: spacing.size4,
});

const imageViewCloseButton = css({
  position: 'absolute',
  top: 30,
  right: 30,
  border: 'none',
  height: 40,
  width: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  cursor: 'pointer',
  zIndex: 1,
  backgroundColor: colors.neutralColors.slate10,
  '&:hover': {
    backgroundColor: colors.neutralColors.slate20,
  },
});

const imageViewerCloseIconStyle = css({
  width: 16,
  height: 16,
});

const spinnerWrapperStyle = () =>
  css({
    position: 'absolute',
    zIndex: 1,
    left: 0,
    right: 0,
    top: '30%',
  });

const ImageModal = ({ imageId, closeImageModal }) => {
  const fileCategory = getFileType(imageId).category;
  const [isLoading, setIsLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    if (fileCategory === FileCategories.PDF && imageId) {
      Storage.get(imageId, { level: 'public' })
        .then((url) => {
          setPdfUrl(url);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }, [imageId, fileCategory]);

  const handleLoad = () => {
    setIsLoading(false);
  };

  return (
    <Overlay isOpen={true} onClick={closeImageModal}>
      <div css={imageViewerContainerStyle}>
        <button
          aria-label="Close image modal"
          css={imageViewCloseButton}
          onClick={closeImageModal}
        >
          <Cross css={imageViewerCloseIconStyle} />
        </button>
        {isLoading && (
          <div css={spinnerWrapperStyle}>
            <Spinner />
          </div>
        )}
        {fileCategory === FileCategories.IMAGE && (
          <S3Image
            css={imageViewerImageStyle}
            level="public"
            imgKey={imageId}
            onLoad={handleLoad}
            style={{ display: isLoading ? 'none' : 'block' }}
          />
        )}
        {fileCategory === FileCategories.PDF && (
          <iframe
            css={pdfViewerStyle}
            src={pdfUrl}
            title="PDF Viewer"
            onLoad={handleLoad}
            style={{ display: isLoading ? 'none' : 'block' }}
          />
        )}

        {fileCategory === FileCategories.OTHER && (
          <div>Unsuported file type.</div>
        )}
      </div>
    </Overlay>
  );
};

ImageModal.propTypes = {
  imageId: PropTypes.string.isRequired,
  closeImageModal: PropTypes.func.isRequired,
};

export { ImageModal };
